// MediaService: normalized file header to avoid BOM/encoding issues
import { storage } from '../firebase/config'
import {
  ref as storageRef,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  getMetadata,
  deleteObject,
  listAll
} from 'firebase/storage'
import PermissionService, { PermissionLevel } from '../firebase/PermissionService'
import StorageQuotaService from '../storage/StorageQuotaService'
import { callAdmin } from '@/services/firebase/callable'
import { useNotify } from '@/composables/useNotify'
import { useUserStore } from '@/stores/userStore'

// Maximum file size (bytes) we'll send through the httpsCallable base64 path.
// Files larger than this will use direct client upload instead to avoid function memory/time issues.
// Increased from 4 MB to 8 MB to allow larger AI-edited images to be sent via the callable.
const MAX_CALLABLE_SIZE_BYTES = 8 * 1024 * 1024 // 8 MB

/**
 * Media Service for handling file uploads to Firebase Storage
 *
 * This service provides a standardized folder structure for all media with permission levels:
 * - /campaigns/{campaignId}/cover - Campaign cover photos (public)
 * - /campaigns/{campaignId}/images/{permission}/{imageId} - Campaign images with permissions
 * - /campaigns/{campaignId}/maps/{permission}/{mapId} - Campaign maps with permissions
 * - /campaigns/{campaignId}/notes/{permission}/{noteId} - Campaign notes with permissions
 * - /campaigns/{campaignId}/pois/{permission}/{poiId} - Points of interest with permissions
 * - /campaigns/{campaignId}/audio/{permission}/{audioId} - Audio files with permissions
 * - /characters/{characterId}/avatar - Character avatar images
 */
class MediaService {
  // Small helper: notify user if file is too large for callable uploads
  _notifyIfTooLarge(file, context = 'file') {
    try {
      const { warning } = useNotify()
      if (file) {
        // Estimate base64-encoded payload size (base64 expands data by ~4/3)
        const est = this.estimateBase64Size(file.size)
        if (est > MAX_CALLABLE_SIZE_BYTES) {
          const mb = Math.round((est / (1024 * 1024)) * 10) / 10
          warning(
            `Selected ${context} will encode to ~${mb} MB (base64) — using direct upload to avoid server limits.`
          )
          return true
        }
      }
      return false
    } catch (e) {
      // If notifications fail for any reason, don't block upload
      console.warn('[MediaService] notify failed', e)
      return false
    }
  }

  // Notify user that quota has been exceeded (safe no-op in tests if useNotify is mocked)
  _notifyQuotaExceeded(storageInfo) {
    try {
      const { warning } = useNotify()
      // Provide a gentle, actionable message when storage info is available
      if (storageInfo && storageInfo.used && storageInfo.limit) {
        const usedMb = Math.round((Number(storageInfo.used || 0) / (1024 * 1024)) * 10) / 10
        const limitMb = Math.round((Number(storageInfo.limit || 0) / (1024 * 1024)) * 10) / 10
        warning(
          `Storage quota exceeded (${usedMb}MB / ${limitMb}MB). Please delete files or upgrade.`
        )
      } else {
        warning('Storage quota exceeded. Please delete files or upgrade your plan.')
      }
      return true
    } catch {
      // swallow any errors (tests may not provide a UI environment)
      return false
    }
  }
  /**
   * Estimate base64-encoded byte length for a binary of given size.
   * Base64 length = 4 * ceil(n / 3)
   * @param {number} byteLength
   * @returns {number}
   */
  estimateBase64Size(byteLength) {
    if (!Number.isFinite(byteLength) || byteLength <= 0) return 0
    return 4 * Math.ceil(byteLength / 3)
  }
  /**
   * Compute SHA-256 hash of a file
   * @param {File|Blob} file - The file to hash
   * @returns {Promise<string>} - Hex string of the hash
   */
  async computeFileHash(file) {
    if (!file) return null
    try {
      const arrayBuffer = await file.arrayBuffer()
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    } catch (err) {
      console.error('[MediaService] Failed to compute file hash:', err)
      return null
    }
  }

  /**
   * Low-level upload of a raw Blob (or File) to a storage path. Returns download URL.
   * Accepts optional contentType override.
   * @param {string} path
   * @param {Blob|File} blob
   * @param {object} [options]
   * @param {string} [options.contentType]
   * @returns {Promise<string>}
   */
  async uploadBlob(path, blob, options = {}) {
    if (!path || !blob) throw new Error('path & blob required')
    try {
      const fileRef = storageRef(storage, path)
      const meta = options.contentType ? { contentType: options.contentType } : undefined
      const snapshot = await uploadBytes(fileRef, blob, meta)
      return await getDownloadURL(snapshot.ref)
    } catch (e) {
      console.error('[MediaService] uploadBlob failed', e)
      throw e
    }
  }

  /**
   * Upload file via signed URL (PUT) using XHR to report progress
   * @param {string} signedUrl
   * @param {File|Blob} file
   * @param {function} progressCallback (pct) => void
   */
  async uploadViaSignedUrl(signedUrl, file, progressCallback, contentTypeOverride) {
    return new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', signedUrl, true)
        xhr.setRequestHeader(
          'Content-Type',
          contentTypeOverride || file.type || 'application/octet-stream'
        )
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && progressCallback) {
            const pct = Math.round((e.loaded / e.total) * 100)
            try {
              progressCallback(pct, e)
            } catch {
              // ignore
            }
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(true)
          else reject(new Error(`Signed upload failed with status ${xhr.status}`))
        }
        xhr.onerror = () => reject(new Error('Signed upload network error'))
        xhr.onabort = () => reject(new Error('Signed upload aborted'))
        xhr.send(file)
      } catch (e) {
        reject(e)
      }
    })
  }
  /**
   * Delete a campaign cover photo
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<void>}
   */
  async deleteCampaignCover(campaignId) {
    if (!campaignId) {
      throw new Error('Campaign ID is required')
    }

    try {
      // Get the cover folder path
      const folderPath = `campaigns/${campaignId}/cover`
      const folderRef = storageRef(storage, folderPath)

      // List all files in the cover folder
      const result = await listAll(folderRef)

      // Delete each file in the folder
      for (const fileRef of result.items) {
        await deleteObject(fileRef)
      }

      return true
    } catch (error) {
      // Ignore error if folder doesn't exist
      if (error.code === 'storage/object-not-found') {
        return true
      }
      console.error('Error deleting campaign cover:', error)
      throw error
    }
  }

  /**
   * Get the download URL of the first cover image for a campaign, if any.
   * Path: campaigns/{campaignId}/cover/*
   * @param {string} campaignId
   * @returns {Promise<string|null>} download URL or null if not found
   */
  async getCampaignCoverUrl(campaignId) {
    if (!campaignId) return null
    try {
      const folderPath = `campaigns/${campaignId}/cover`
      const folderRef = storageRef(storage, folderPath)
      const result = await listAll(folderRef)
      if (!result || !Array.isArray(result.items) || result.items.length === 0) return null
      // Use the first item (or prefer the last for most recent)
      const itemRef = result.items[result.items.length - 1] || result.items[0]
      const url = await getDownloadURL(itemRef)
      return url || null
    } catch (e) {
      // If folder doesn't exist or access denied, return null silently
      return null
    }
  }

  /**
   * Delete all avatar files for a character (best-effort).
   * Safe to call even if the character has no avatar or folder.
   * @param {string} characterId
   * @returns {Promise<boolean>} true if done (or nothing to delete)
   */
  async deleteCharacterAvatar(characterId) {
    if (!characterId) return false
    try {
      const folderPath = `characters/${characterId}/avatar`
      const folderRef = storageRef(storage, folderPath)
      const listing = await listAll(folderRef)
      for (const item of listing.items) {
        try {
          await deleteObject(item)
        } catch (e) {
          // Ignore individual delete errors to make this best-effort
          console.warn(
            '[MediaService] Failed to delete avatar item',
            item?.fullPath || item?.name,
            e
          )
        }
      }
      return true
    } catch (error) {
      // If folder or objects don't exist, treat as success
      if (
        error &&
        (error.code === 'storage/object-not-found' ||
          /not[- ]found/i.test(String(error.message || '')))
      ) {
        return true
      }
      console.error('[MediaService] Error deleting character avatar folder:', error)
      return false
    }
  }

  /**
   * Delete a campaign resource
   * @param {string} resourceType - The resource type
   * @param {string} campaignId - The campaign ID
   * @param {Object} resource - The resource object
   * @returns {Promise<void>}
   */
  async deleteCampaignResource(resourceType, campaignId, resource) {
    if (!resourceType || !campaignId || !resource) {
      throw new Error('Resource type, campaign ID, and resource object are required')
    }

    // Determine path from resource
    let path
    if (resource.path) {
      // If path is already provided in resource
      path = resource.path
    } else {
      // Otherwise construct it from components
      const permissionLevel = resource.permission?.level || PermissionLevel.CAMPAIGN
      const fileName = resource.fileName || resource.id
      path = PermissionService.getStoragePath(resourceType, campaignId, permissionLevel, fileName)
    }

    // Delete the file
    return this.deleteFile(path)
  }

  /**
   * Generic resource deletion helper for campaigns and characters.
   * Accepts either an explicit storage path or a resource object and context.
   * @param {Object} options - { resourceType, campaignId, characterId, resource }
   * @returns {Promise<boolean>}
   */
  async deleteResource({
    resourceType = null,
    campaignId = null,
    characterId = null,
    resource = null
  } = {}) {
    // If caller provided an explicit resource.path inside resource, use it
    try {
      if (resource && resource.path) {
        try {
          await this.deleteFile(resource.path)
          return true
        } catch (e) {
          // If the object wasn't found at the recorded path, try to resolve via URL or alternate extension
          if (e && e.code === 'storage/object-not-found') {
            const candidateUrl =
              resource.url &&
              (typeof resource.url === 'string'
                ? resource.url
                : resource.url.url ||
                  resource.url.downloadUrl ||
                  resource.url.finalUrl ||
                  resource.url.path ||
                  resource.url.fullPath)
            if (candidateUrl) {
              const ok = await this.deleteByUrl(candidateUrl)
              if (ok) return true
            }
            // Try swapping common extensions (e.g., .png -> .jpg)
            try {
              const p = String(resource.path)
              if (p.endsWith('.png')) {
                await this.deleteFile(p.slice(0, -4) + '.jpg')
                return true
              }
            } catch {
              /* ignore */
            }
          }
          throw e
        }
      }

      // Campaign resource
      if (resourceType && campaignId) {
        const permissionLevel = resource?.permission?.level || PermissionLevel.CAMPAIGN
        const fileName = (resource && (resource.fileName || resource.id)) || null
        if (!fileName)
          throw new Error('fileName or resource.id required to delete campaign resource')
        const path = PermissionService.getStoragePath(
          resourceType,
          campaignId,
          permissionLevel,
          fileName
        )
        await this.deleteFile(path)
        return true
      }

      // Character resource (gallery or other)
      if (resourceType && characterId) {
        const fileName = (resource && (resource.fileName || resource.id)) || null
        if (!fileName)
          throw new Error('fileName or resource.id required to delete character resource')
        const path =
          resourceType === 'gallery'
            ? `characters/${characterId}/gallery/${fileName}`
            : `characters/${characterId}/${resourceType}/${resource?.permission?.level || PermissionLevel.CHARACTER}/${fileName}`
        await this.deleteFile(path)
        return true
      }

      throw new Error('Insufficient information to delete resource')
    } catch (err) {
      console.error('[MediaService] deleteResource failed', err)
      throw err
    }
  }

  /**
   * List all resources of a specific type with a permission level in a campaign
   * @param {string} resourceType - The resource type
   * @param {string} campaignId - The campaign ID
   * @param {string} permissionLevel - The permission level
   * @returns {Promise<Array>} - Array of download URLs
   */
  async listCampaignResources(resourceType, campaignId, permissionLevel) {
    if (!resourceType || !campaignId || !permissionLevel) {
      throw new Error('Resource type, campaign ID, and permission level are required')
    }

    const path = `campaigns/${campaignId}/${resourceType}/${permissionLevel}`
    const folderRef = storageRef(storage, path)

    try {
      const result = await listAll(folderRef)

      // Get download URLs for all items
      const items = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef)
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            url
          }
        })
      )

      return items
    } catch (error) {
      // If folder doesn't exist, return empty array
      if (error.code === 'storage/object-not-found') {
        return []
      }
      throw error
    }
  }

  /**
   * Helper: get the storage folder for user character sheets
   * @param {string} userId
   */
  _characterSheetFolder(userId) {
    if (!userId) throw new Error('userId required')
    // Storage rules and existing uploads use users/{userId}/charactersheets
    return `users/${userId}/charactersheets`
  }

  /**
   * List all character sheets for a given user.
   * Returns an array of { name, fullPath, url, metadata: { size, uploadedAt, path, hash } }
   */
  async listCharacterSheets(userId) {
    if (!userId) throw new Error('userId required')

    // Try canonical folder first, but fall back to legacy/common variants to
    // support older uploads or migration inconsistencies.
    const candidates = [
      this._characterSheetFolder(userId),
      `user-charactersheet/${userId}`,
      `users/${userId}/charactersheet`,
      `users/${userId}/character-sheets`,
      `users/${userId}/charactersheet`
    ]

    let listing = null
    for (const cand of candidates) {
      try {
        const folderRef = storageRef(storage, cand)
        const l = await listAll(folderRef)
        if (l && Array.isArray(l.items) && l.items.length > 0) {
          listing = l
          break
        }
      } catch {
        // ignore and try next candidate
      }
    }

    // If no listing found on any candidate, return empty list
    if (!listing) return []

    const out = []
    for (const itemRef of listing.items) {
      try {
        const url = await getDownloadURL(itemRef)

        // Best-effort metadata read; SDK metadata access may vary by environment.
        let size = null
        let uploadedAt = null
        let hash = null
        try {
          const meta = await getMetadata(itemRef)
          size = meta?.size ? Number(meta.size) : null
          uploadedAt = meta?.timeCreated || meta?.updated || null
          hash = meta?.customMetadata?.hash || null
        } catch (metaErr) {
          console.debug('[MediaService] getMetadata failed', metaErr)
        }

        // Strip leading timestamp/hash segments (e.g., "1759632376171_filename.pdf" or "abcd1234_filename.pdf")
        const originalName = itemRef.name || ''
        let displayName = originalName
        try {
          const sepMatch = originalName.match(/[_-]/)
          if (sepMatch) {
            const sep = sepMatch[0]
            const parts = originalName.split(/[_-]/)
            if (parts.length > 1) {
              const first = parts[0]
              const isTimestamp = /^\d{8,}$/.test(first)
              const isHash = /^[0-9a-fA-F]{6,}$/.test(first)
              if (isTimestamp || isHash) {
                displayName = parts.slice(1).join(sep || '_')
              }
            }
          }
        } catch (displayErr) {
          console.debug('[MediaService] display name parse failed', displayErr)
        }

        const typeFromMeta =
          typeof getMetadata === 'function'
            ? await (async () => {
                try {
                  const m = await getMetadata(itemRef)
                  return m?.contentType || null
                } catch {
                  return null
                }
              })()
            : null

        out.push({
          // Keep `name` as the original filename so caller logic (duplicate detection, file creation)
          // that compares against File.name continues to work. Provide `displayName` for UI.
          name: originalName,
          displayName: displayName,
          fullPath: itemRef.fullPath,
          url,
          metadata: {
            originalName,
            size,
            uploadedAt,
            path: itemRef.fullPath,
            hash,
            type: typeFromMeta
          }
        })
      } catch (itemErr) {
        // Best-effort: still include minimal entry
        console.debug('[MediaService] listCharacterSheets item read failed', itemErr)
        try {
          const url = await this.getDownloadUrlForPath(itemRef.fullPath)
          out.push({
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            url,
            metadata: { size: null, uploadedAt: null, path: itemRef.fullPath, hash: null }
          })
        } catch {
          // If even URL resolution fails, include minimal path-only entry
          out.push({
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            url: null,
            metadata: { size: null, uploadedAt: null, path: itemRef.fullPath, hash: null }
          })
        }
      }
    }

    return out
  }

  /**
   * Upload a character sheet for a user. Returns { url, path, metadata }
   * If a duplicate is detected (same name+size or same hash) returns { isDuplicate: true, url, path, metadata }
   */
  async uploadCharacterSheet(userId, file, { checkDuplicates = true, progress } = {}) {
    if (!userId) throw new Error('userId required')
    if (!file) throw new Error('file required')

    // Compute storage path using canonical folder and original filename
    const safeName = String(file.name || 'sheet').replace(/^\/+|\.+\//g, '')
    const storagePath = `${this._characterSheetFolder(userId)}/${safeName}`

    // Compute a hash for duplicate detection (best-effort)
    let hash = null
    try {
      hash = await this.computeFileHash(file)
    } catch {
      hash = null
    }

    if (checkDuplicates) {
      try {
        const existing = await this.listCharacterSheets(userId)
        const byNameAndSize = existing.find(
          (e) => e.name === file.name && Number(e?.metadata?.size || 0) === Number(file.size || 0)
        )
        const byHash = hash
          ? existing.find((e) => e?.metadata?.hash && e.metadata.hash === hash)
          : null
        const dup = byHash || byNameAndSize
        if (dup)
          return { isDuplicate: true, url: dup.url, path: dup.fullPath, metadata: dup.metadata }
      } catch {
        /* ignore duplicate check failures */
      }
    }

    const uploaded = await this.uploadManaged({
      storagePath,
      file,
      userId,
      resourceType: 'character-sheet',
      makePublic: true,
      progress,
      customMetadata: {
        hash: hash || '',
        size: String(file.size || 0),
        uploadedAt: new Date().toISOString()
      }
    })

    // uploadManaged returns either a URL string or an object; normalize
    try {
      const url =
        typeof uploaded === 'string'
          ? uploaded
          : uploaded?.url || uploaded?.downloadUrl || uploaded?.path || null
      return {
        url: url || null,
        path: storagePath,
        metadata: {
          hash: hash || '',
          size: Number(file.size || 0),
          uploadedAt: new Date().toISOString()
        }
      }
    } catch {
      return {
        url: null,
        path: storagePath,
        metadata: {
          hash: hash || '',
          size: Number(file.size || 0),
          uploadedAt: new Date().toISOString()
        }
      }
    }
  }

  /**
   * Delete a character sheet by storage path or public URL
   */
  async deleteCharacterSheet(pathOrUrl) {
    if (!pathOrUrl) return false
    if (typeof pathOrUrl === 'string' && pathOrUrl.startsWith('http'))
      return this.deleteByUrl(pathOrUrl)
    await this.deleteFile(pathOrUrl)
    return true
  }

  /**
   * General file upload method
   * @param {string} path - Storage path
   * @param {File} file - File to upload
   * @param {Object} options - Upload options
   * @param {string} options.userId - User ID for quota tracking (required)
   * @param {string} options.resourceType - Resource type for categorization (optional)
   * @param {boolean} options.skipQuotaCheck - Skip quota check (for admin operations)
   * @returns {Promise<string>} - URL of the uploaded file
   */
  async uploadFile(path, file, options = {}, progressCallback) {
    try {
      // Determine whether to skip quota checks: explicit option OR current user is admin
      let skipQuota = !!options.skipQuotaCheck
      try {
        const userStore = useUserStore()
        if (userStore && userStore.isAdmin) skipQuota = true
      } catch {
        // If Pinia isn't available in this context, fall back to provided option only
      }

      // Check storage quota before upload (unless explicitly skipped)
      if (!skipQuota && options.userId) {
        const fileSize = file.size
        const quotaCheck = await StorageQuotaService.checkQuota(options.userId, fileSize)

        if (!quotaCheck.allowed) {
          // Notify user about quota exceeded
          this._notifyQuotaExceeded(quotaCheck.storageInfo)
          // Do not notify here; let callers (stores/components) handle user messaging
          const err = new Error(quotaCheck.reason || 'Storage quota exceeded')
          err.code = 'quota-exceeded'
          err.storageInfo = quotaCheck.storageInfo
          throw err
        }
      }

      // Get file reference
      const fileRef = storageRef(storage, path) // Changed from ref to storageRef

      let uploadedFileSize = file.size
      let snapshot

      // If resize is requested and it's an image
      if (options.resize && file.type.startsWith('image/')) {
        const resizedFile = await this.resizeImage(file, options.maxWidth, options.maxHeight)
        uploadedFileSize = resizedFile.size

        // Check quota again with resized file size if it changed significantly
        if (!skipQuota && options.userId && Math.abs(uploadedFileSize - file.size) > 1024) {
          const quotaCheck = await StorageQuotaService.checkQuota(options.userId, uploadedFileSize)
          if (!quotaCheck.allowed) {
            // Notify user about quota exceeded
            this._notifyQuotaExceeded(quotaCheck.storageInfo)
            const err = new Error(quotaCheck.reason || 'Storage quota exceeded after resize')
            err.code = 'quota-exceeded'
            err.storageInfo = quotaCheck.storageInfo
            try {
              err.userNotified = true
            } catch {
              /* ignore */
            }
            throw err
          }
        }

        // Upload the resized file (resumable so we can report progress)
        snapshot = await new Promise((resolve, reject) => {
          try {
            const uploadTask = uploadBytesResumable(fileRef, resizedFile)
            uploadTask.on(
              'state_changed',
              (snap) => {
                if (progressCallback && snap.totalBytes) {
                  const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                  try {
                    progressCallback(pct, snap)
                  } catch {
                    // ignore progress callback errors
                  }
                }
              },
              (err) => reject(err),
              () => resolve(uploadTask.snapshot)
            )
          } catch (e) {
            reject(e)
          }
        })
      } else {
        // Upload original file (resumable so we can report progress)
        snapshot = await new Promise((resolve, reject) => {
          try {
            const uploadTask = uploadBytesResumable(fileRef, file)
            uploadTask.on(
              'state_changed',
              (snap) => {
                if (progressCallback && snap.totalBytes) {
                  const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                  try {
                    progressCallback(pct, snap)
                  } catch {
                    // ignore progress callback errors
                  }
                }
              },
              (err) => reject(err),
              () => resolve(uploadTask.snapshot)
            )
          } catch (e) {
            reject(e)
          }
        })
      }

      const downloadUrl = await getDownloadURL(snapshot.ref)

      // Track successful upload in user's storage quota only when not skipping quota checks (legacy fallback).
      // Normally, rely on Storage triggers to update all aggregations in one write.
      if (options.userId && !skipQuota) {
        try {
          await StorageQuotaService.trackUpload(options.userId, uploadedFileSize, {
            type: options.resourceType || 'other',
            path,
            uploadedAt: new Date().toISOString()
          })
        } catch (trackError) {
          // Log but don't fail the upload if tracking fails
          console.error('[MediaService] Failed to track storage usage:', trackError)
        }
      }

      return downloadUrl
    } catch (error) {
      console.error('Error in file upload:', error)
      throw error
    }
  }

  /**
   * Upload a file via the backend callable `uploadFile` which writes to Storage server-side.
   * This avoids client-side direct uploads and ensures server triggers see the file.
   * @param {string} storagePath
   * @param {File|Blob} file
   * @param {object} options
   * @returns {Promise<string>} download URL
   */
  async uploadViaCallable(storagePath, file, options = {}, progressCallback) {
    if (!storagePath || !file) throw new Error('storagePath & file required')

    // Determine whether to skip quota checks: explicit option OR current user is admin
    let skipQuotaCallable = !!options.skipQuotaCheck
    try {
      const userStore = useUserStore()
      if (userStore && userStore.isAdmin) skipQuotaCallable = true
    } catch {
      // ignore
    }

    // Enforce quota before server-side upload to avoid bypassing client checks
    if (!skipQuotaCallable && options.userId) {
      const quotaCheck = await StorageQuotaService.checkQuota(options.userId, file.size)
      if (!quotaCheck.allowed) {
        // User-friendly notify
        this._notifyQuotaExceeded(quotaCheck.storageInfo)
        const err = new Error(quotaCheck.reason || 'Storage quota exceeded')
        err.code = 'quota-exceeded'
        err.storageInfo = quotaCheck.storageInfo
        try {
          err.userNotified = true
        } catch {
          /* ignore */
        }
        throw err
      }
    }

    // Convert file to base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result || ''
        const comma = String(result).indexOf(',')
        resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result))
      }
      reader.onerror = (e) => reject(e)
      reader.readAsDataURL(file)
    })

    const payload = {
      storagePath,
      base64,
      contentType: options.contentType || file.type || 'application/octet-stream',
      makePublic: options.makePublic === true,
      customMetadata: options.customMetadata || undefined
    }

    // If file is large, attempt signed-upload URL flow to avoid base64 payloads
    try {
      const est = this.estimateBase64Size(file.size)
      const SIGNED_URL_THRESHOLD = 6 * 1024 * 1024 // 6MB base64-estimated threshold -> tuneable
      if (est > SIGNED_URL_THRESHOLD) {
        try {
          const sigRes = await callAdmin('getSignedUploadUrl', {
            storagePath,
            contentType: file.type || 'application/octet-stream',
            expectedSize: file.size
          })
          const signed = sigRes?.data || null
          if (signed && signed.url) {
            // Upload via signed URL using XHR so we can report progress
            // If server returned a tempPath, upload to that path; otherwise fall back to storagePath
            const tempPath = signed.tempPath || storagePath
            await this.uploadViaSignedUrl(signed.url, file, progressCallback, options.contentType)
            // Call finalize to copy temp -> final and perform server-side metadata actions
            const fin = await callAdmin('finalizeSignedUpload', {
              tempPath,
              finalPath: storagePath,
              makePublic: options.makePublic === true,
              expectedSize: file.size
            })
            const finalData = fin?.data || {}
            const downloadUrl = finalData.url || null

            // Track quota usage only when not skipping; prefer triggers for aggregation
            if (options.userId && !skipQuotaCallable) {
              try {
                await StorageQuotaService.trackUpload(options.userId, file.size, {
                  type: options.resourceType || 'other',
                  path: storagePath,
                  uploadedAt: new Date().toISOString()
                })
              } catch (trackError) {
                console.warn(
                  '[MediaService] Failed to track storage usage after signed upload:',
                  trackError
                )
              }
            }

            return downloadUrl
          }
        } catch (sErr) {
          // If signed URL flow fails, log and continue to callable/fallback logic
          console.warn(
            '[MediaService] Signed URL request/upload failed, will attempt callable or fallback',
            sErr
          )
        }
      }
    } catch (e) {
      // non-fatal
      console.warn('[MediaService] signed-url precheck failed', e)
    }

    // Try callable, but fallback to direct client upload on size/network/time errors
    try {
      const res = await callAdmin('uploadFile', payload, { timeout: 120000, retries: 1 })
      const url = res?.data?.url || res?.data?.downloadUrl || null

      if (!url) throw new Error('uploadFile callable did not return a download URL')

      // Track quota usage (callable path) only when not skipping; prefer triggers for aggregation
      if (options.userId && !skipQuotaCallable) {
        try {
          await StorageQuotaService.trackUpload(options.userId, file.size, {
            type: options.resourceType || 'other',
            path: storagePath,
            uploadedAt: new Date().toISOString()
          })
        } catch (trackError) {
          console.warn(
            '[MediaService] Failed to track storage usage after callable upload:',
            trackError
          )
        }
      }

      return url
    } catch (err) {
      // Decide if we should fallback to client-side upload
      const msg = String(err?.message || '')
      const code = err?.code || err?.httpErrorCode?.status || ''

      const isQuotaError = code === 'quota-exceeded' || /quota/i.test(msg)
      const isPayloadTooLarge =
        /payload\s*too\s*large|entity\s*too\s*large|request\s*entity\s*too\s*large|\b413\b/i.test(
          msg
        )
      const isTimeoutOrOom = /(deadline|timeout|memory|oom)/i.test(msg)
      const isNetworkReset = /ECONNRESET|ETIMEDOUT|network/i.test(msg)
      const shouldFallback =
        !isQuotaError && (isPayloadTooLarge || isTimeoutOrOom || isNetworkReset)

      // Do not fallback on auth/permission or business errors
      const isPermissionError = /permission|unauthoriz/i.test(msg) || code === 'permission-denied'

      if (shouldFallback && !isPermissionError) {
        // Notify user and attempt client upload fallback which will perform quota tracking
        try {
          this._notifyIfTooLarge({ size: file.size }, 'file')
        } catch {
          // ignore
        }
        console.warn('[MediaService] uploadViaCallable failed, falling back to client upload', {
          err
        })

        // Use client upload helper which includes metadata and quota tracking
        const downloadUrl = await this.uploadFile(storagePath, file, options, progressCallback)
        return downloadUrl
      }

      // Otherwise rethrow the original error
      try {
        const msg = String(err?.message || '')
        const code = err?.code || ''
        if (code === 'quota-exceeded' || /quota/i.test(msg)) {
          this._notifyQuotaExceeded(err?.storageInfo)
          try {
            err.userNotified = true
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
      throw err
    }
  }

  /**
   * Unified managed upload entry point to centralize policy, quota notify, and flow.
   * @param {Object} args
   * @param {string} args.storagePath - Final storage path to write
   * @param {File|Blob} args.file - File/blob to upload
   * @param {string} args.userId - User ID for quota checks/tracking
   * @param {string} [args.resourceType] - For quota breakdown
   * @param {boolean} [args.makePublic=true]
   * @param {function} [args.progress] - Progress callback
   * @param {string} [args.preDeleteFolderPath] - If set, delete all items under this folder before upload
   * @param {Object} [args.customMetadata] - Custom metadata to set on object
   * @param {number} [args.maxBytes] - If set, reject files bigger than this many bytes
   * @param {boolean} [args.convertToJpeg=true] - If true and file is an image, convert to JPEG (keeps PNG with alpha)
   * @param {number} [args.jpegQuality=0.92] - JPEG quality when converting (0..1)
   * @returns {Promise<string>} download URL
   */
  async uploadManaged({
    storagePath,
    file,
    userId,
    resourceType,
    makePublic = true,
    progress,
    preDeleteFolderPath,
    customMetadata,
    maxBytes,
    convertToJpeg = true,
    jpegQuality = 0.92,
    deleteAfterSuccess
  } = {}) {
    if (!storagePath || !file) throw new Error('storagePath & file required')

    // Optional size guard
    if (Number.isFinite(maxBytes) && file.size > maxBytes) {
      const err = new Error('File exceeds maximum allowed size')
      err.code = 'file-too-large'
      err.maxBytes = maxBytes
      err.size = file.size
      throw err
    }

    // Optional cleanup for single-item folders (e.g., avatar folders)
    if (preDeleteFolderPath) {
      try {
        const folderRef = storageRef(storage, preDeleteFolderPath)
        const listing = await listAll(folderRef)
        for (const item of listing.items) {
          try {
            await deleteObject(item)
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
    }

    // Default image conversion to JPEG (preserve PNG with alpha). Do not change storagePath name.
    let workingFile = file
    let didConvertToJpeg = false
    try {
      const mime = String(file.type || '')
      const isImage = mime.startsWith('image/')
      if (convertToJpeg && isImage) {
        // Convert all raster images except SVG/GIF to JPEG
        const isSvg = mime === 'image/svg+xml'
        const isGif = mime === 'image/gif'
        const isJpeg = mime === 'image/jpeg'
        if (!isSvg && !isGif && !isJpeg) {
          const converted = await this.convertImageBlob(file, 'image/jpeg', jpegQuality)
          if (converted) {
            // Preserve filename metadata where possible
            try {
              workingFile = new File([converted], file.name, {
                type: 'image/jpeg',
                lastModified: file.lastModified
              })
              didConvertToJpeg = true
            } catch {
              // Environments without File constructor: fall back to Blob
              Object.defineProperty(converted, 'type', { value: 'image/jpeg', configurable: true })
              workingFile = converted
              didConvertToJpeg = true
            }
          }
        }
      }
    } catch {
      // Non-fatal; continue with original file
      workingFile = file
    }

    // If converted to JPEG, ensure storagePath ends with .jpg for consistency
    if (didConvertToJpeg) {
      try {
        const parts = storagePath.split('/')
        const name = parts.pop() || ''
        const dot = name.lastIndexOf('.')
        const newName = dot > -1 ? name.slice(0, dot) + '.jpg' : name + '.jpg'
        storagePath = [...parts, newName].join('/')
      } catch {
        /* ignore path rewrite errors */
      }
    }

    // Perform the upload (callable/signed-url/fallback)
    // Seed progress so UI doesn't stay at 0 for server-side (callable) uploads
    try {
      if (typeof progress === 'function') progress(0)
    } catch {
      /* ignore */
    }
    const rawDownload = await this.uploadViaCallable(
      storagePath,
      workingFile,
      {
        userId,
        resourceType: resourceType || 'other',
        makePublic,
        customMetadata,
        contentType: didConvertToJpeg ? 'image/jpeg' : undefined
      },
      progress
    )

    // Normalize the returned download value so we never return an object to callers
    let downloadUrl = null
    try {
      if (typeof rawDownload === 'string') downloadUrl = rawDownload
      else if (rawDownload && typeof rawDownload === 'object') {
        downloadUrl =
          rawDownload.url ||
          rawDownload.downloadUrl ||
          rawDownload.path ||
          rawDownload.fullPath ||
          null
        if (typeof downloadUrl !== 'string') downloadUrl = null
      }
    } catch {
      downloadUrl = null
    }

    // Post-upload cleanup (best-effort). Supports:
    // - deleteAfterSuccess.paths: array of storage paths or public URLs to delete
    // - deleteAfterSuccess.folderExcept: { folderPath, keepFileName }
    if (deleteAfterSuccess) {
      ;(async () => {
        try {
          if (Array.isArray(deleteAfterSuccess.paths)) {
            for (const p of deleteAfterSuccess.paths) {
              try {
                // Normalize possible object inputs into string path/url
                let pStr = null
                if (typeof p === 'string') pStr = p
                else if (p && typeof p === 'object') {
                  pStr =
                    p.url ||
                    p.downloadUrl ||
                    p.path ||
                    p.storagePath ||
                    p.finalUrl ||
                    p.fullPath ||
                    null
                }
                if (!pStr) continue

                // If this is a URL, use deleteByUrl which will decode it
                if (pStr.startsWith('http')) {
                  await this.deleteByUrl(pStr)
                } else {
                  // Only delete if it's not the newly uploaded path
                  if (pStr !== storagePath) await this.deleteFile(pStr)
                }
              } catch (e) {
                console.warn('[MediaService] deleteAfterSuccess path failed', p, e)
              }
            }
          }

          if (deleteAfterSuccess.folderExcept && deleteAfterSuccess.folderExcept.folderPath) {
            try {
              const { folderPath, keepFileName } = deleteAfterSuccess.folderExcept
              // If keepFileName not provided, compute from final storagePath
              const finalKeep =
                keepFileName || (storagePath ? storagePath.split('/').pop() : undefined)
              await this.cleanupFolderExcept(folderPath, finalKeep)
            } catch (e) {
              console.warn('[MediaService] deleteAfterSuccess folderExcept failed', e)
            }
          }
        } catch (e) {
          // swallow cleanup errors
          console.warn('[MediaService] post-upload cleanup failed', e)
        }
      })()
    }

    // If we couldn't normalize to a downloadable URL, fall back to returning the storage path
    // Ensure progress reaches 100% for UI consumers (best-effort)
    try {
      if (typeof progress === 'function') progress(100)
    } catch {
      /* ignore */
    }
    return { url: downloadUrl || storagePath, path: storagePath }
  }

  /**
   * Delete a file from Firebase Storage
   * @param {string} path - Storage path
   * @returns {Promise<void>}
   * @private
   */
  async deleteFile(path) {
    const fileRef = storageRef(storage, path)

    try {
      await deleteObject(fileRef)
    } catch (error) {
      // Ignore error if file doesn't exist
      if (error.code === 'storage/object-not-found') {
        return
      }
      throw error
    }
  }

  /**
   * Delete all objects under a folder except the specified file name (best-effort)
   * @param {string} folderPath - storage folder path (no leading/trailing slashes)
   * @param {string} keepFileName - filename to keep (optional)
   */
  async cleanupFolderExcept(folderPath, keepFileName) {
    if (!folderPath) return
    try {
      const folderRef = storageRef(storage, folderPath)
      const listing = await listAll(folderRef)
      for (const item of listing.items) {
        if (keepFileName && item.name === keepFileName) continue
        try {
          await deleteObject(item)
        } catch {
          /* ignore individual delete errors */
        }
      }
      return true
    } catch {
      // If folder doesn't exist or other error, ignore (best-effort)
      return false
    }
  }

  /**
   * Attempt to delete an object given a Firebase download URL
   * Supports URLs of the form: https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encodedPath>?alt=media&token=...
   * Also accepts storage full paths (e.g., 'campaigns/xxx/cover/file.jpg') and gs:// urls
   * @param {string} url
   */
  async deleteByUrl(url) {
    if (!url) return false
    try {
      // Normalize if an object was passed
      let u = url
      if (u && typeof u === 'object') {
        u = u.url || u.downloadUrl || u.path || u.storagePath || u.finalUrl || u.fullPath || null
      }
      if (!u) return false

      // If it's already a storage path (not an http/gs url)
      if (typeof u === 'string' && !u.startsWith('http') && !u.startsWith('gs://')) {
        await this.deleteFile(u)
        return true
      }

      // Ensure we have a string to operate on
      const s = String(u)

      // Try to extract the /o\/(.+?)(?:\?|$)/ component
      const m = s.match(/\/o\/(.+?)(?:\?|$)/)
      if (m && m[1]) {
        const decoded = decodeURIComponent(m[1])
        await this.deleteFile(decoded)
        return true
      }

      // gs://bucket/path
      const gs = s.match(/^gs:\/\/(?:[^/]+)\/(.+)$/)
      if (gs && gs[1]) {
        await this.deleteFile(gs[1])
        return true
      }

      // Could not resolve path
      return false
    } catch {
      // best-effort
      return false
    }
  }

  /**
   * Return a download URL for a storage path
   * @param {string} path
   */
  async getDownloadUrlForPath(path) {
    if (!path) return null
    try {
      return await getDownloadURL(storageRef(storage, path))
    } catch {
      return null
    }
  }

  /**
   * Get size (bytes) for an object by its public download URL or storage path
   * Note: Firebase client SDK doesn't expose metadata read-by-url; this is best-effort and may return null
   */
  async getSizeByUrl(urlOrPath) {
    try {
      // Try to resolve to a storage path
      let path = urlOrPath
      if (urlOrPath && urlOrPath.startsWith('http')) {
        const m = String(urlOrPath).match(/\/o\/(.+?)(?:\?|$)/)
        if (m && m[1]) path = decodeURIComponent(m[1])
        else path = null
      }
      if (!path) return null
      // The client SDK doesn't provide a direct metadata fetch without using the Admin SDK or REST API.
      // As a lightweight fallback, attempt getDownloadURL then HEAD request to read Content-Length.
      const downloadUrl = await this.getDownloadUrlForPath(path)
      if (!downloadUrl) return null
      try {
        const resp = await fetch(downloadUrl, { method: 'HEAD' })
        if (!resp.ok) return null
        const len = resp.headers.get('content-length')
        return len ? Number(len) : null
      } catch {
        return null
      }
    } catch {
      return null
    }
  }

  /**
   * Get basic image info (width, height, mime) from a download URL or storage path
   */
  async getImageInfo(urlOrPath) {
    try {
      let downloadUrl = urlOrPath
      if (!String(urlOrPath).startsWith('http')) {
        downloadUrl = await this.getDownloadUrlForPath(urlOrPath)
      }
      if (!downloadUrl) return null
      // If no DOM (SSR/tests), we cannot create Image — bail out
      if (typeof window === 'undefined' || typeof Image === 'undefined') return null
      return await new Promise((resolve) => {
        const img = new Image()
        img.onload = () =>
          resolve({
            width: img.naturalWidth,
            height: img.naturalHeight,
            mime: img.src?.includes('.svg') ? 'image/svg+xml' : 'image/*'
          })
        img.onerror = () => resolve(null)
        img.src = downloadUrl
      })
    } catch {
      return null
    }
  }

  /**
   * Resize an image file to specified dimensions
   * @param {File} file - Original image file
   * @param {number} maxWidth - Maximum width
   * @param {number} maxHeight - Maximum height
   * @returns {Promise<File>} - Resized file
   * @private
   */
  async resizeImage(file, maxWidth, maxHeight) {
    // In non-DOM environments (SSR/tests) we cannot perform canvas resizing; return original file
    if (typeof document === 'undefined') return Promise.resolve(file)

    return new Promise((resolve, reject) => {
      // Create file reader to read the file
      const reader = new FileReader()
      reader.onload = (event) => {
        // Create an image element to use for resizing
        const img = new Image()
        img.onload = () => {
          // Determine new dimensions maintaining aspect ratio
          let width = img.width
          let height = img.height

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width)
            width = maxWidth
          }

          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height)
            height = maxHeight
          }

          // Create canvas and resize
          try {
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height

            // Draw resized image on canvas
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)

            // Convert to blob and create new file
            canvas.toBlob((blob) => {
              try {
                const resizedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: file.lastModified
                })
                resolve(resizedFile)
              } catch {
                // Environments without File constructor: return blob
                resolve(blob)
              }
            }, file.type)
          } catch (err) {
            reject(err)
          }
        }

        img.onerror = () => {
          reject(new Error('Failed to load image for resizing'))
        }

        // Load image from data URL
        img.src = event.target.result
      }

      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }

      // Read file as data URL
      reader.readAsDataURL(file)
    })
  }

  /**
   * Convert an image Blob/File to a different MIME using Canvas.
   * Falls back to Image+ObjectURL if createImageBitmap is unavailable.
   * @param {Blob|File} blobOrFile
   * @param {string} mime - target mime (e.g., 'image/jpeg')
   * @param {number} quality - 0..1 for JPEG/WebP
   * @returns {Promise<Blob|null>}
   */
  async convertImageBlob(blobOrFile, mime = 'image/jpeg', quality = 0.92) {
    try {
      if (typeof createImageBitmap === 'function') {
        const bmp = await createImageBitmap(blobOrFile)
        // Prefer OffscreenCanvas in non-DOM environments
        if (typeof document === 'undefined' && typeof OffscreenCanvas === 'function') {
          try {
            const off = new OffscreenCanvas(bmp.width, bmp.height)
            const ctx = off.getContext('2d')
            ctx.drawImage(bmp, 0, 0)
            if (typeof off.convertToBlob === 'function')
              return await off.convertToBlob({ type: mime, quality })
            return null
          } catch {
            /* fall through */
          }
        }
        if (typeof document === 'undefined') return null
        const canvas = document.createElement('canvas')
        canvas.width = bmp.width
        canvas.height = bmp.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(bmp, 0, 0)
        return await new Promise((resolve) => canvas.toBlob(resolve, mime, quality))
      }
    } catch {
      // fall through to Image fallback
    }

    // Fallback: use HTMLImageElement
    return await new Promise((resolve) => {
      try {
        const img = new Image()
        img.onload = () => {
          try {
            // If no DOM, cannot perform Image fallback
            if (typeof document === 'undefined') return resolve(null)
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth || img.width
            canvas.height = img.naturalHeight || img.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            canvas.toBlob((b) => resolve(b), mime, quality)
          } catch {
            resolve(null)
          }
        }
        img.onerror = () => resolve(null)
        const url = URL.createObjectURL(blobOrFile)
        img.src = url
        // Clean up the object URL on load/error
        img.addEventListener('load', () => URL.revokeObjectURL(url), { once: true })
        img.addEventListener('error', () => URL.revokeObjectURL(url), { once: true })
      } catch {
        resolve(null)
      }
    })
  }

  /**
   * Quick alpha detection for PNG: returns true if any alpha < 255 in a 64x64 sample.
   * @param {Blob|File} blobOrFile
   * @returns {Promise<boolean>}
   */
  async pngHasAlpha(blobOrFile) {
    try {
      const bmp =
        typeof createImageBitmap === 'function' ? await createImageBitmap(blobOrFile) : null
      if (bmp) {
        const w = Math.min(64, bmp.width)
        const h = Math.min(64, bmp.height)
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.drawImage(bmp, 0, 0, w, h)
        const { data } = ctx.getImageData(0, 0, w, h)
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) return true
        }
        return false
      }
    } catch {
      // fall back to Image
    }

    return await new Promise((resolve) => {
      try {
        const img = new Image()
        img.onload = () => {
          try {
            const w = Math.min(64, img.naturalWidth || img.width)
            const h = Math.min(64, img.naturalHeight || img.height)
            const canvas = document.createElement('canvas')
            canvas.width = w
            canvas.height = h
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, w, h)
            const { data } = ctx.getImageData(0, 0, w, h)
            for (let i = 3; i < data.length; i += 4) {
              if (data[i] < 255) return resolve(true)
            }
            resolve(false)
          } catch {
            resolve(false)
          }
        }
        img.onerror = () => resolve(false)
        const url = URL.createObjectURL(blobOrFile)
        img.src = url
        img.addEventListener('load', () => URL.revokeObjectURL(url), { once: true })
        img.addEventListener('error', () => URL.revokeObjectURL(url), { once: true })
      } catch {
        resolve(false)
      }
    })
  }
}

export default new MediaService()
